import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import buildPlaylist, { PlaylistItem } from './buildPlaylist';

// Resolves once the image is downloaded and decoded (or failed), so swapping to
// it doesn't flash an empty frame. Only the next frame is preloaded: a decoded
// 4K screenshot is ~30MB, too much to hold for a whole wallboard on a TV stick.
const preloadImage = (url: string): Promise<void> =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      if (image.decode) {
        image.decode().then(resolve, () => resolve());
      } else {
        resolve();
      }
    };
    image.onerror = () => resolve();
    image.src = url;
  });

const getItemFrame = ({
  manifest,
  item,
}: {
  manifest: WallboardScreenshots.Manifest;
  item: PlaylistItem | undefined;
}): WallboardScreenshots.Frame | undefined =>
  item ? manifest.slides[item.slideIndex]?.frames[item.frameIndex] : undefined;

/**
 * Steps through the playlist, preloading each screenshot before showing it.
 * Returns the item on screen and the previous screenshot's URL (kept under the
 * new one while it fades in).
 */
const usePlaylistPlayback = ({
  manifest,
}: {
  manifest: WallboardScreenshots.ManifestResponse;
}) => {
  const playlist = useMemo(() => buildPlaylist({ manifest }), [manifest]);
  const [position, setPosition] = useState<number>(0);
  const [previousUrl, setPreviousUrl] = useState<string | undefined>();
  // Counts every frame change, so overlays (the leaderboard countdown) restart
  // even when the same frame comes round again.
  const [showCount, setShowCount] = useState<number>(0);
  const safePosition = playlist.length > 0 ? position % playlist.length : 0;
  const item = playlist[safePosition] as PlaylistItem | undefined;
  const currentUrl = getItemFrame({ manifest, item })?.url;
  const isAdvancingRef = useRef<boolean>(false);

  const advance = useCallback(async () => {
    if (playlist.length === 0 || isAdvancingRef.current) {
      return;
    }
    isAdvancingRef.current = true;
    const nextPosition = (safePosition + 1) % playlist.length;
    const nextUrl = getItemFrame({
      manifest,
      item: playlist[nextPosition],
    })?.url;
    if (nextUrl) {
      await preloadImage(nextUrl);
    }
    setPreviousUrl(currentUrl);
    setPosition(nextPosition);
    setShowCount((count) => count + 1);
    isAdvancingRef.current = false;
  }, [currentUrl, manifest, playlist, safePosition]);

  useEffect(() => {
    if (!item || playlist.length <= 1) {
      return;
    }
    const timeout = setTimeout(advance, item.durationMs);
    return () => {
      clearTimeout(timeout);
    };
  }, [advance, item, playlist.length]);

  return { item, previousUrl, advance, playKey: String(showCount) };
};

export default usePlaylistPlayback;
